/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { ClientApiProps, ICallsignApplicationEx, ICallsignTransferExt, yupPhoneTestOptions } from '../../common';
import {
  AddressPicker,
  AddressSchema,
  Button,
  Checkbox,
  ErrorScope,
  FieldSet,
  FormContext,
  FormPage,
  FormPageContext,
  IconButton,
  Label,
  PhoneInput,
  RadioList,
  RadioListOption,
  SecurePay,
  SecurePayMethods,
  Select,
  SelectOption,
  TextArea,
  TextInput,
  TokenisedCard,
  ValidationHint,
} from 'acma-spa/dist/';
import {
  Acma_amateurradiocallsignavailability,
  Acma_amateurradioassessmenttypes,
  Acma_amateurradiocallsign_statuscode,
  Acma_amateurradiocallsignprefixes,
  Acma_amateurradiocallsigntypes,
  Acma_amateurradiolocations,
  IOperatorAssessment,
  IOperatorCallsign,
  getApiClient,
  Address,
} from '../../api';
import { CallsignPicker } from '../../common/CallsignPicker';
import { AuthorisationModal } from '../../common/AuthorisationModal';
import DatePicker from 'react-multi-date-picker';
import DatePanel from 'react-multi-date-picker/plugins/date_panel';
import { PublicClientApplication } from '@azure/msal-browser';
import {
  Acma_amateurradioassessment_statuscode,
  Acma_amateurradioassessortypes,
  Acma_amateurradioreservationtypes,
  ICallsignTransfer,
  IChargingRule,
  IsClientIdentifierValidResult,
  IsEmailAddressValidResult,
} from '../../api/client';
import { Link } from '../../common/FormHelpers';
import { HelpModal } from '../../common/HelpModal';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const createSchema = (baseUrl: string, b2cDomainName: string, msalClient: PublicClientApplication) => {
  const [client] = getApiClient(baseUrl, b2cDomainName, msalClient);

  return yup.object({
    id: yup.string(),
    referenceNumber: yup.string(),
    clientIdentificationNumber: yup.number().typeError("Invalid client identification number").test(
      'clientid-backend-validation',
      'Invalid client identification number',
      async (value, context) => {
        if (value === undefined || value == null) {
          return false;
        }
        else {
          const valid = await client.validateTransferClientidentifier(context.parent.callSignName, value.toString());

          if (valid == IsClientIdentifierValidResult.InvalidClient)
            return context.createError({ message: 'The client identification number provided could not be found' });

          if (valid == IsClientIdentifierValidResult.InvalidTransferToSelf)
            return context.createError({ message: 'The callsign cannot be transferred to yourself' });

          return true;
        }
      }
    ),
    firstName: yup.string().label('First name').required(),
    lastName: yup.string().label('Last name').required(),
    emailAddress: yup.string().email().label('Email address').required().test(
      'emailaddress-backend-validation',
      'Invalid email address',
      async (value, context) => {
        if (value === undefined || value == null) {
          return false;
        }
        else {
          const valid = await client.validateTransferEmailAddress(context.parent.callSignName, value.toString());

          if (valid == IsEmailAddressValidResult.Invalid)
            return context.createError({ message: 'The email address provided is not valid' });

          if (valid == IsEmailAddressValidResult.InvalidTransferToSelf)
            return context.createError({ message: 'The callsign cannot be transferred to yourself' });

          return true;
        }
      }
    ),
    otherName: yup.string(),
    status: yup.mixed<Acma_amateurradiocallsign_statuscode>().oneOf(Object.values(Acma_amateurradiocallsign_statuscode)).optional(),
    confirmation1: yup.boolean().required('Confirmation required').isTrue('Confirmation required'),
    confirmation2: yup.boolean().required('Confirmation required').isTrue('Confirmation required'),
    confirmation3: yup.boolean().isTrue('Confirmation required')
      .when(['clubRepresentative'], {
        is: (clubRepresentative?: boolean) => clubRepresentative == true,
        then: (schema) => schema.required('Confirmation required'),
      }),
    assessmentReferenceNumber: yup.string(),
    clubRepresentative: yup.boolean().required('Confirmation required'),
    clubName: yup
      .string()
      .label('Club name')
      .when(['clubRepresentative'], {
        is: (clubRepresentative?: boolean) => clubRepresentative == true,
        then: (schema) => schema.required(),
      }),
    qualificationLevel: yup.string().label('Qualification level').required(),
    tokenisedCard: yup.string(),
    callSignId: yup.string(),
    callSignName: yup.string(),
  });
};

export interface CallsignTransferApplicationPageProps extends ClientApiProps {
  ctx: FormContext<ICallsignTransferExt>;
  fee: number | undefined;
  callsignname: string | undefined;
}

export const getLocationOptions = () => {
  const options: SelectOption<Acma_amateurradiolocations>[] = [];
  for (const enumMember in Acma_amateurradiolocations) {
    options.push({
      value: enumMember as Acma_amateurradiolocations,
      label: enumMember == Acma_amateurradiolocations.AustralianExternalTerritories ? 'Australian External Territories' : enumMember,
    });
  }
  return options;
};

const yesNoOptions: RadioListOption<boolean>[] = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

export const CallsignTransferApplicationPage: React.FC<CallsignTransferApplicationPageProps> = ({ ctx, ...props }) => {
  const securePayRef = useRef<SecurePayMethods>(null);

  return (
    <FormPage<ICallsignTransferExt>
      ctx={ctx}
      schema={createSchema(props.baseUrl, props.b2cDomainName, props.msalClient) as yup.ObjectSchema<ICallsignTransferExt>}
      onNext={async () => {
        if (securePayRef.current != null) {
          securePayRef.current.tokenise();
        }
        return false;
      }}>
      {(ctx: FormPageContext<ICallsignTransferExt>) => {
        /**
         * Note: React expects "Updater Functions" to update useState hooks.
         * However, FormPage does not allow this when calling setModel(). We can only pass a raw value.
         * eg: setModel(x+1) vs setModel(x => x+1)
         * @see: https://react.dev/reference/react/useState
         */
        const { model, setModel, errors } = ctx;
        model.callSignName = props.callsignname;

        const [tokenisedCard, setTokenisedCard] = useState<TokenisedCard | null>(null);

        const getFee = (model: ICallsignApplicationEx): number | undefined => {
          return props.fee;
        };

        const [contentModal, setContentModal] = useState(false);
        const toggleContent = () => setContentModal(!contentModal);

        useEffect(() => {
          if (!tokenisedCard) {
            return;
          }
          ctx.onNavigate({ ...model, tokenisedCard: tokenisedCard.token }, true, true);
        }, [tokenisedCard]);

        return (
          <>
            <div className="mb-3">
              Please complete this form and supply supporting documents, where applicable if you would like to apply to transfer your call sign to a different amateur operator. Your application will be processed in accordance with the {Link(props.contentUrls.examRules, 'Radiocommunications (Amateur Stations) Class Licence 2023')} and the {Link(props.contentUrls.callSignPolicy, 'ACMA call sign policy')}. Once you submit this form you will receive an email from the ACMA confirming that your application has been received.
            </div>

            <p>
              Provide the details of the call sign you want to be transferred and the name and contact details of the operator you want to transfer your call
              sign to. Please ensure that you provide the correct information - incorrect information may delay our consideration of your application.
            </p>

            <p>You will need to pay a fee to have your application considered.</p>

            <p>
              If the amateur operator who you would like to transfer your call sign to does not
              hold adequate qualifications to be transferred this call sign, the application may not be approved.
            </p>

            <h2 className="my-2">Details of the Operator the call sign will be transferred to</h2>

            {
              <div>
                <div className="row">
                  <div className="col-sm-4 mb-3">
                    <Label id="firstName" label="First name" error={errors?.firstName} required={true}>
                      <TextInput readonly={ctx.readOnly} value={model.firstName || ''} onChange={(v) => setModel({ firstName: v })} maxLength={149} />
                    </Label>
                  </div>
                  <div className="col-sm-4 mb-3">
                    <Label id="lastName" label="Last name" error={errors?.lastName} required={true}>
                      <TextInput readonly={ctx.readOnly} value={model.lastName || ''} onChange={(v) => setModel({ lastName: v })} maxLength={149} />
                    </Label>
                  </div>
                  <div className="col-sm-4 mb-3">
                    <Label id="otherName" label="Other name" error={errors?.otherName}>
                      <TextInput readonly={ctx.readOnly} value={model.otherName || ''} onChange={(v) => setModel({ otherName: v })} maxLength={149} />
                    </Label>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-4 mb-3">
                    <Label id="emailAddress" label="Email address" error={errors?.emailAddress} required={true}>
                      <TextInput readonly={ctx.readOnly} value={model.emailAddress || ''} onChange={(v) => setModel({ emailAddress: v })} maxLength={400} />
                    </Label>
                  </div>
                </div>
              </div>
            }

            <div className="row">
              <div className="col-sm-4 mb-3">
                <FieldSet label="Are you applying on behalf of a club?" error={errors?.clubRepresentative} required={true}>
                  <RadioList<boolean | undefined>
                    id="clubRepresentative"
                    disabled={ctx.readOnly}
                    options={yesNoOptions}
                    value={model.clubRepresentative}
                    onChange={(v) => setModel({ clubRepresentative: v })}
                  />
                </FieldSet>
              </div>
              {model.clubRepresentative && (
                <div className="col-sm-8 mb-3">
                  <Label id="clubName" label="Name of the club you are applying to transfer this call sign to" error={errors?.clubName} required={true}>
                    <TextInput readonly={ctx.readOnly} value={model.clubName || ''} onChange={(v) => setModel({ clubName: v })} maxLength={400} />
                  </Label>
                </div>
              )}
            </div>

            <div className="row">
              <div className="col-sm-4 mb-3">
                <Label
                  id="qualificationLevel"
                  label="Qualification level of Operator call sign will be transferred to"
                  error={errors?.qualificationLevel}
                  required>
                  <TextInput
                    readonly={ctx.readOnly}
                    value={model.qualificationLevel || ''}
                    onChange={(v) => setModel({ qualificationLevel: v })}
                    maxLength={10}
                  />
                </Label>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-4 mb-3">
                <Label
                  id="clientIdentificationNumber"
                  label={<>ACMA Client number of Operator the call sign will be transferred to&nbsp; <IconButton onClick={() => toggleContent()} mode={faInfoCircle} label={''} /></>}
                  error={errors?.clientIdentificationNumber}
                  required>
                  <TextInput
                    readonly={ctx.readOnly}
                    value={model.clientIdentificationNumber || ''}
                    onChange={(v) => setModel({ clientIdentificationNumber: v })}
                    maxLength={10}
                  />
                </Label>
              </div>
            </div>

            {!ctx.readOnly ? (
              <>
                <h2 className="my-2">Payment details</h2>
                <h4 className="my-2" style={{ color: 'red' }}>Your payment will be put on hold until the amateur operator you have applied to transfer your call sign to has accepted the transfer.
                  As funds can only be held for a short period of time, your payment will timeout after 1-3 days (depending on your bank) if we do not receive a response.</h4>
                <h4>Total: ${getFee(model)} AUD</h4>
                <SecurePay
                  clientId={props.securePayClientId ?? ''}
                  merchantCode={props.securePayMerchantCode ?? ''}
                  uri={`${props.securePaySrc}`}
                  onLoadComplete={() => { }}
                  onTokeniseError={() => ctx.onLoading(null)}
                  onTokeniseSuccess={(v) => {
                    ctx.onLoading(null);
                    setTokenisedCard(v);
                  }}
                  ref={securePayRef}
                />
              </>
            ) : (
              <></>
            )}

            <hr />

            <div className="row">
              <div className="col mb-3">
                <ErrorScope id="confirmation1" error={errors?.confirmation1}>
                  <Checkbox
                    id="confirmation1"
                    label="I confirm that the information provided is true and correct."
                    checked={ctx.readOnly ? true : model.confirmation1 ?? false}
                    onChange={(v) => setModel({ confirmation1: v })}
                    disabled={ctx.readOnly}
                    required
                  />
                </ErrorScope>
              </div>
            </div>

            <div className="row">
              <div className="col mb-3">
                <ErrorScope id="confirmation2" error={errors?.confirmation2}>
                  <Checkbox
                    id="confirmation2"
                    label="I understand that if the transfer of this call sign is successful, the ACMA will cancel my assignment of this call sign and I will be unable to use the call sign."
                    checked={ctx.readOnly ? true : model.confirmation2 ?? false}
                    onChange={(v) => setModel({ confirmation2: v })}
                    disabled={ctx.readOnly}
                    required
                  />
                </ErrorScope>
              </div>
            </div>

            {model.clubRepresentative && <div className="row">
              <div className="col mb-3">
                <ErrorScope id="confirmation3" error={errors?.confirmation3}>
                  <Checkbox
                    id="confirmation3"
                    label={
                      <>
                        I confirm that I have read the amateur radio {Link(props.contentUrls.callSignPolicy, 'Call sign policy')} and the club that I want to
                        transfer this call sign to is considered to be a 'legal person' consistent with the call sign policy. I also understand that the
                        representative of the club is an advanced amateur operator.
                      </>
                    }
                    checked={ctx.readOnly ? true : model.confirmation3 ?? false}
                    onChange={(v) => setModel({ confirmation3: v })}
                    disabled={ctx.readOnly}
                    required
                  />
                </ErrorScope>
              </div>
            </div>}

            <div className="alert alert-info" role="alert">
              The personal information being collected in this form is to enable the ACMA to decide whether it will transfer this call sign to another amateur operator consistent with the {Link(props.contentUrls.callSignPolicy, 'ACMA Call sign policy')}. The information will also be used by the ACMA to administer call signs and facilitate call sign applications and assignments.
              <br />
              <br />
              The ACMA publishes a call sign register on its website that lists all call signs and, if the call sign is allocated, the respective qualification level associated with the allocated call sign. The call sign register does not contain any other information about a licence holder.
              <br />
              <br />
              The ACMA will not use any of the personal information you provide in this form for any other purpose, nor will we disclose it, unless we have your consent or we are otherwise permitted to do so under the <em>Privacy Act 1988</em>.
              <br />
              <br />
              Further information about the {Link(props.contentUrls.privacyPolicy, "Privacy Act and the ACMA's Privacy Policy")} is on our website. The Privacy Policy contains details about how you may access personal information about you that is held by the ACMA, and seek the correction of such information. It also explains how you may complain about a breach of the {Link(props.contentUrls.privacyAct, 'Privacy Act')} and how we will deal with such a complaint.
            </div>

            <HelpModal open={contentModal} onClose={() => setContentModal(false)} title={'Information'} />
          </>
        );
      }}
    </FormPage>
  );
};
